import * as THREE from 'three';

const BALL_RADIUS = 0.3;
const BALL_COLOR = 0x1a1a1a;
const TRAIL_COLOR = 0x555555;
const SPLASH_COLOR = 0xffffff;
const TRAIL_LENGTH = 6;
const SPLASH_DURATION = 0.4;
/** Approximate server patch period; used to interpolate between snapshots. */
const PATCH_PERIOD = 1 / 20;

interface CannonballLike {
  id: number;
  x: number;
  y: number;
  z: number;
}

interface BallRender {
  group: THREE.Group;
  prev: THREE.Vector3;
  target: THREE.Vector3;
  age: number;
}

interface Splash {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  life: number;
}

/**
 * Client-side renderer for all active cannonballs.
 *
 * Each ball is a dark sphere with a short motion trail. Positions arrive from
 * the server as discrete snapshots, so the renderer keeps the previous and
 * latest snapshot and lerps between them between patches. When a ball leaves
 * server state near the water surface, a short-lived splash ring is shown.
 */
export class CannonballRenderer {
  private readonly scene: THREE.Scene;
  private readonly balls = new Map<number, BallRender>();
  private readonly splashes: Splash[] = [];
  private readonly ballGeo: THREE.SphereGeometry;
  private readonly ballMat: THREE.MeshStandardMaterial;
  private readonly trailGeo: THREE.SphereGeometry;
  private readonly trailMat: THREE.MeshBasicMaterial;
  private readonly splashGeo: THREE.RingGeometry;
  private readonly splashMat: THREE.MeshBasicMaterial;
  private readonly forward = new THREE.Vector3(0, 0, 1);
  private readonly velocity = new THREE.Vector3();
  /** Reused each frame to track which server balls are still present. */
  private readonly seenIds = new Set<number>();

  /**
   * Optional callback fired when a ball first appears in server state. The
   * `position` vector is transient and reused — copy it before returning.
   */
  onSpawn?: (position: THREE.Vector3) => void;

  /**
   * Optional callback fired when a ball leaves server state, before its mesh
   * is recycled. `nearWater` is true when the removal point is close to the
   * water surface (the existing splash ring is also spawned in that case).
   * The `position` vector is transient — copy it before returning.
   */
  onRemoved?: (position: THREE.Vector3, nearWater: boolean) => void;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.ballGeo = new THREE.SphereGeometry(BALL_RADIUS, 8, 6);
    this.ballMat = new THREE.MeshStandardMaterial({
      color: BALL_COLOR,
      roughness: 0.4,
      metalness: 0.6,
    });
    this.trailGeo = new THREE.SphereGeometry(BALL_RADIUS * 0.5, 4, 3);
    this.trailGeo.scale(1, 1, TRAIL_LENGTH);
    this.trailMat = new THREE.MeshBasicMaterial({
      color: TRAIL_COLOR,
      transparent: true,
      opacity: 0.4,
    });
    this.splashGeo = new THREE.RingGeometry(0.15, 0.9, 12);
    this.splashMat = new THREE.MeshBasicMaterial({
      color: SPLASH_COLOR,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    });
  }

  /** Sync visible cannonballs with the server state collection. */
  update(
    delta: number,
    serverBalls: Iterable<CannonballLike>,
    waterHeight: (x: number, z: number) => number,
  ): void {
    this.seenIds.clear();

    for (const ball of serverBalls) {
      this.seenIds.add(ball.id);
      let render = this.balls.get(ball.id);
      if (!render) {
        render = this.createBall();
        this.balls.set(ball.id, render);
        this.scene.add(render.group);
        render.prev.set(ball.x, ball.y, ball.z);
        render.target.set(ball.x, ball.y, ball.z);
        render.group.position.set(ball.x, ball.y, ball.z);
        render.age = PATCH_PERIOD;
        this.onSpawn?.(render.group.position);
      } else {
        const dx = ball.x - render.target.x;
        const dy = ball.y - render.target.y;
        const dz = ball.z - render.target.z;
        if (dx * dx + dy * dy + dz * dz > 1e-8) {
          render.prev.copy(render.target);
          render.target.set(ball.x, ball.y, ball.z);
          render.age = 0;
        }
      }
    }

    // Remove balls no longer in server state and spawn splashes near the water.
    for (const [id, render] of this.balls) {
      if (!this.seenIds.has(id)) {
        const position = render.group.position;
        const surfaceY = waterHeight(position.x, position.z);
        const nearWater = position.y < surfaceY + 2;
        this.onRemoved?.(position, nearWater);
        if (nearWater) {
          this.spawnSplash(position.x, surfaceY + 0.05, position.z);
        }
        this.scene.remove(render.group);
        this.balls.delete(id);
      }
    }

    // Interpolate every surviving ball between its two latest snapshots.
    for (const render of this.balls.values()) {
      render.age += delta;
      const t = Math.min(1, render.age / PATCH_PERIOD);
      render.group.position.lerpVectors(render.prev, render.target, t);
      this.velocity.copy(render.target).sub(render.prev);
      if (this.velocity.lengthSq() > 1e-10) {
        render.group.quaternion.setFromUnitVectors(this.forward, this.velocity.normalize());
      }
    }

    // Animate and retire splashes.
    for (let i = this.splashes.length - 1; i >= 0; i -= 1) {
      const splash = this.splashes[i];
      splash.life -= delta;
      if (splash.life <= 0) {
        this.scene.remove(splash.mesh);
        splash.material.dispose();
        this.splashes.splice(i, 1);
      } else {
        const t = splash.life / SPLASH_DURATION;
        splash.mesh.scale.setScalar(1 + (1 - t) * 1.6);
        splash.material.opacity = t * 0.7;
      }
    }
  }

  private createBall(): BallRender {
    const group = new THREE.Group();

    const mesh = new THREE.Mesh(this.ballGeo, this.ballMat);
    mesh.castShadow = true;
    group.add(mesh);

    // Elongated sphere behind the ball; the group is oriented along velocity.
    const trail = new THREE.Mesh(this.trailGeo, this.trailMat);
    trail.position.z = -TRAIL_LENGTH * 0.3;
    group.add(trail);

    return {
      group,
      prev: new THREE.Vector3(),
      target: new THREE.Vector3(),
      age: 0,
    };
  }

  private spawnSplash(x: number, y: number, z: number): void {
    const material = this.splashMat.clone();
    const mesh = new THREE.Mesh(this.splashGeo, material);
    mesh.position.set(x, y, z);
    mesh.rotation.x = -Math.PI / 2;
    this.scene.add(mesh);
    this.splashes.push({ mesh, material, life: SPLASH_DURATION });
  }

  dispose(): void {
    for (const [, render] of this.balls) {
      this.scene.remove(render.group);
    }
    this.balls.clear();
    for (const splash of this.splashes) {
      this.scene.remove(splash.mesh);
      splash.material.dispose();
    }
    this.splashes.length = 0;
    this.ballGeo.dispose();
    this.ballMat.dispose();
    this.trailGeo.dispose();
    this.trailMat.dispose();
    this.splashGeo.dispose();
    this.splashMat.dispose();
  }
}
